import React, { useEffect, useState } from "react";
import { Card, Input } from "antd";
import { toast } from "react-toastify";
import CommonSelect, { Option } from "../../../core/common/commonSelect";
import moment from "moment";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { _get, _post, server_url } from "../../Utils/Helper";
import { SingleValue } from "react-select";
import { Button, Col, Row, Spinner } from "react-bootstrap";

// Payment Interface
interface Payment {
  amount: number;
  term: string;
  academic_year: string;
  admission_no: string;
  school_id: string;
  item_code: string;
  description: string;
  status: string;
  is_optional: string;
  student_type: string;
  discount: number;
  fines: number;
  checked: boolean;
  amount_paid: number;
  ref_no: string;
}

// Props
interface StudentPayModalsProps {
  admission_no?: string;
  academic_year: string;
  term: string;
  student_name: string;
}

const ParentPayModals: React.FC<StudentPayModalsProps> = ({
  admission_no,
  student_name,
  academic_year,
  term,
}) => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [selectedRows, setSelectedRows] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);

  const [form, setForm] = useState({
    payment_mode: "Cash Deposite",
    query_type: "update-paid",
    // admission_no: admission_no,
    created_by: user?.name || "",
    payment_date: moment().format("YYYY-MM-DD"),
    status: "Paid",
    academic_year: academic_year,
    term: term,
    receipt_url: "",
  });

  // Fetch student payment data
  useEffect(() => {
    if (admission_no) {
      setLoading(true);
      _post(
        "payments",
        {
          query_type: "select",
          admission_no,
          term: term,
          academic_year: academic_year,
        },
        (resp) => {
          if (resp.success) {
            const paymentData = resp.data.map((x: Payment) => ({
              ...x,
              checked: x.is_optional === "No" || x.status !== "Paid",
            }));
            setPayments(paymentData);
            setSelectedRows(paymentData.filter((p: any) => p.checked));
          }
          setLoading(false);
        },
        (err) => {
          console.error(err);
          setLoading(false);
        }
      );
    }
  }, [admission_no, term, academic_year]);

  // Handle Selection
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows([...payments]);
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelection = (
    e: React.ChangeEvent<HTMLInputElement>,
    payment: Payment
  ) => {
    setSelectedRows((prev) =>
      prev.some((p) => p.item_code === payment.item_code)
        ? prev.filter((p) => p.item_code !== payment.item_code)
        : [...prev, payment]
    );
  };

  const totalAmount = selectedRows.reduce(
    (sum, payment) => sum + Math.floor(payment.amount),
    0
  );
  const totalDiscount = selectedRows.reduce(
    (sum, payment) => sum + Math.floor(payment.discount),
    0
  );
  const totalFines = selectedRows.reduce(
    (sum, payment) => sum + Math.floor(payment.fines),
    0
  );

  // Handle Submit
  const handleSubmit = () => {
    const paymentData = selectedRows.map((payment) => ({
      ...payment,
      amount_paid: payment.amount,
      total: totalAmount - totalDiscount,
      admission_no: admission_no || "",
      ...form,
    }));

    setLoading(true);
    _post(
      "payments",
      paymentData,
      (resp) => {
        if (resp.success) {
          toast.success("Payment processed successfully");
          setPayments([]);
          setSelectedRows([]);
        }
        setLoading(false);
      },
      (err) => {
        toast.error("Payment processing failed");
        console.error(err);
        setLoading(false);
      }
    );
    if (form.receipt_url !== "") {
      _post(
        "receipt-url",
        {
          query_type: "create",
          ref_no: selectedRows[0].ref_no,
          url: form.receipt_url,
        },
        (resp) => {
          if (resp.success) {
            toast.success("Receipt url uploaded successfully");
          }
        },
        (err) => {
          console.error(err);
        }
      );
    }
  };

  const isAllSelected =
    selectedRows.length > 0 && selectedRows.length === payments.length;
  const [isUploading, setIsUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string>("");
  // Handle file selection and upload
  const onChangeUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const attachmentFile = e.target.files ? e.target.files[0] : null;
    if (!attachmentFile) return;

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (attachmentFile.size > maxSize) {
      toast.error("File size exceeds the 5MB limit.");
      return;
    }

    // Validate file type: allow JPG, PNG, and PDF
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    if (!allowedTypes.includes(attachmentFile.type)) {
      toast.error("Only JPG, PNG, or PDF files are allowed.");
      return;
    }

    // If the file is an image, generate a preview; if PDF, show a PDF logo.
    if (attachmentFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(attachmentFile);
    } else if (attachmentFile.type === "application/pdf") {
      // Set a default PDF logo as the preview
      setImagePreview(
        "https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg"
      ); // Update this path to your PDF icon's location
    }

    // Prepare form data for upload
    setIsUploading(true);
    const formData = new FormData();
    formData.append("media", attachmentFile);

    // Upload the file
    fetch(server_url + "/upload_transfer_certificate-document", {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        setForm((prevForm: any) => ({ ...prevForm, receipt_url: data.url }));
        toast.success("Uploaded successfully");
      })
      .catch((error) => {
        console.error("Error uploading attachment:", error);
        toast.error("Failed to upload the file.");
      })
      .finally(() => {
        setIsUploading(false);
      });
  };

  return (
    <div className="modal fade" id="student_payment_modal">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {"Process Payment".toUpperCase()}

              <div className="ml-8 mb-3">
                {student_name} ({admission_no})
              </div>
            </h5>
            <button
              type="button"
              className="btn-close bg-danger text-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div className="modal-body">
            <Row>
              <Col md={6}>
                <p>
                  <strong>Academic Year:</strong>{" "}
                  <span className="">{academic_year}</span>
                </p>
              </Col>
              <Col md={6}>
                <p>
                  <strong>Term:</strong> <span className="">{term}</span>
                </p>
              </Col>
            </Row>
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>
                      <Input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Paid</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments
                    .filter((x: Payment) => x.amount > 0)
                    .map((payment: Payment) => (
                      <tr key={payment.item_code}>
                        <td>
                          {payment.item_code}
                          <Input
                            type="checkbox"
                            checked={selectedRows.some(
                              (p) => p.item_code === payment.item_code
                            )}
                            onChange={(e) => handleRowSelection(e, payment)}
                          />
                        </td>
                        <td>{payment.description}</td>
                        <td>{payment.amount}</td>
                        <td>{payment.amount_paid}</td>
                        <td>
                          {payment.status === "Paid" ? (
                            <span className="badge badge-soft-success d-inline-flex align-items-center">
                              <i className="ti ti-circle-filled fs-5 me-1"></i>
                              {payment.status}
                            </span>
                          ) : (
                            <span className="badge badge-soft-danger d-inline-flex align-items-center">
                              <i className="ti ti-circle-filled fs-5 me-1"></i>
                              {payment.status}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex justify-content-between mt-3 row">
              <Card>
                <div className="col-md-12">
                  <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                    <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                      {imagePreview ? (
                        <img src={imagePreview} width={80} height={80} />
                      ) : (
                        <i className="ti ti-photo-plus fs-16" />
                      )}
                    </div>
                    <div className="profile-upload">
                      <div className="profile-uploader d-flex align-items-center">
                        <div className="drag-upload-btn mb-3">
                          Upload
                          <input
                            type="file"
                            onChange={onChangeUpload}
                            className="form-control image-sign"
                          />
                        </div>
                        <Button
                          onClick={() => {
                            setForm((p) => ({ ...p, receipt_url: "" }));
                            setImagePreview("");
                          }}
                          className="btn btn-primary mb-3"
                        >
                          Remove
                        </Button>
                      </div>
                      <p className="fs-12">
                        {isUploading ? (
                          <span>Uploading...</span>
                        ) : imagePreview ? (
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={form.receipt_url}
                          >
                            Click to Preview
                          </a>
                        ) : (
                          "Upload image size 4MB, Format PDF, JPG, PNG, SVG"
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
              <div className="col-md-4">
                <p>Total Amount: {totalAmount}</p>
                <p>
                  Discount:
                  <Input
                    name="discount"
                    type="number"
                    disabled={true}
                    className={"py-2"}
                    value={totalDiscount}
                  />
                </p>
              </div>

              <div className="col-md-4 text-left">
                <br />
                <br />
                <label className="">Payment Mode:</label>
                <CommonSelect
                  placeholder="Select Payment Mode"
                  className="select "
                  options={[
                    { label: "Bank Transfer", value: "Bank Transfer" },
                    { label: "Cash Deposite", value: "Cash Deposite" },
                    { label: "POS", value: "POS" },
                    { label: "Scholarship", value: "Scholarship" },
                  ]}
                  defaultValue={{
                    label: form.payment_mode,
                    value: form.payment_mode,
                  }}
                  handleChange={(newValue: SingleValue<Option>) =>
                    setForm((prev) => ({
                      ...prev,
                      payment_mode: newValue ? newValue.value : "",
                    }))
                  }
                />
              </div>
              <div className="col-md-4">
                <br />
                <br />
                <p className="p-0 m-0">
                  Total Payable: {totalAmount - totalDiscount}
                </p>
                <Button
                  color="primary"
                  onClick={() => handleSubmit()}
                  disabled={loading}
                >
                  {loading ? <Spinner /> : "Submit Payment"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentPayModals;
